import React from "react";
import { observer } from "mobx-react-lite";
import { Typography } from "@mui/material";

type SectionWiseResponseProps = {
  response: Record<string, any>;
  lang?: string;
};

const safe_get = (value: any, preferredLang = "en"): string | null => {
  if (!value) return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        const parsed = JSON.parse(trimmed);
        return parsed?.[preferredLang] || parsed?.ml || null;
      } catch {
        return trimmed || null;
      }
    }
    return trimmed || null;
  }

  if (typeof value === "object") {
    return value?.[preferredLang] || value?.ml || null;
  }

  return null;
};

const renderValue = (value: any, lang: string): JSX.Element | null => {
  if (Array.isArray(value)) {
    return (
      <ul className="pl-4 list-disc">
        {value.map((item, idx) => (
          <li key={idx}>{renderValue(item, lang)}</li>
        ))}
      </ul>
    );
  }

  if (value && typeof value === "object") {
    return (
      <div className="ml-4 space-y-1">
        {Object.entries(value).map(([key, val], idx) => (
          <div key={idx}>
            <strong className="capitalize">{key.replace(/_/g, " ")}:</strong>{" "}
            {renderValue(val, lang)}
          </div>
        ))}
      </div>
    );
  }

  const text = safe_get(value, lang);
  return text ? <span>{text}</span> : null;
};

export const SectionWiseResponse = observer(
  ({ response, lang = "en" }: SectionWiseResponseProps) => {
    return (
      <div className="p-2">
        {Object.entries(response).map(([sectionKey, sectionValue]) => (
          <div key={sectionKey} className="mb-4">
            <Typography variant="h6" className="capitalize font-semibold mb-1">
              {sectionKey.replace(/_/g, " ")}
            </Typography>

            {Array.isArray(sectionValue)
              ? sectionValue.map((item, idx) => (
                  <div key={idx} className="mb-2">
                    {renderValue(item, lang)}
                  </div>
                ))
              : renderValue(sectionValue, lang)}
          </div>
        ))}
      </div>
    );
  },
);
